import type { ComponentProps } from 'react';
import {
  Children,
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import styled from 'styled-components';
import { invariant, useStableRect } from '@elfsight/widget-utils';
import useMergedRef from '@react-hook/merged-ref';

type Justification =
  | 'start'
  | 'end'
  | 'center'
  | 'stretch'
  | 'space-around'
  | 'space-between'
  | 'space-evenly';

export const GridContainer = styled.div<{
  _justification?: Justification;
}>`
  display: grid;
  justify-content: ${(props) => props._justification};
`;

export type GridProps = ComponentProps<typeof GridContainer> & {
  columns?: 'auto' | number;
  basis?: number;
  gap?: number | [number, number];
  justification?: Justification;
  onEffectiveColumnsChange: (effectiveColumns: number) => void;
};

export const Grid = forwardRef<HTMLDivElement, GridProps>(
  (
    {
      children,
      columns = 'auto',
      basis = 300,
      gap = 0,
      justification = 'stretch',
      onEffectiveColumnsChange,
      style,
      ...forwardingProps
    },
    forwardingRef
  ) => {
    const columnsFixed = typeof columns === 'number';
    const columnsAuto = columns === 'auto';

    invariant(
      columnsFixed || columnsAuto,
      'Expected the columns to be auto or a number.'
    );

    let columnGap: number | undefined;
    let rowGap: number | undefined;

    if (Array.isArray(gap)) {
      invariant(
        gap.length === 2,
        'If an array passed the gap is expected to be exactly 2 values long ' +
          'which correspondingly define column and row gaps.'
      );

      [columnGap, rowGap] = gap;
    } else {
      columnGap = gap;
      rowGap = gap;
    }

    const [effectiveColumns, setEffectiveColumns] = useState(
      columnsFixed ? (columns as number) : undefined
    );

    // handle effective columns change callback
    const onEffectiveColumnsChangeRef = useRef(onEffectiveColumnsChange);

    useEffect(() => {
      onEffectiveColumnsChangeRef.current = onEffectiveColumnsChange;
    }, [onEffectiveColumnsChange]);

    useEffect(() => {
      if (
        onEffectiveColumnsChangeRef.current &&
        effectiveColumns !== undefined
      ) {
        onEffectiveColumnsChangeRef.current(effectiveColumns);
      }
    }, [effectiveColumns]);

    const [containerWidth, setContainerWidth] = useState(0);
    const [containerRectRef] = useStableRect(
      { width: containerWidth },
      ({ width }) => setContainerWidth(width)
    );

    const containerRef = useRef<HTMLDivElement | null>(null);

    // combine refs
    const containerMergedRef = useMergedRef(
      containerRectRef,
      containerRef,
      forwardingRef
    );

    // compute the effective columns
    useLayoutEffect(() => {
      const { current: containerElement } = containerRef;

      if (!containerElement) {
        return;
      }

      const { length: nextEffectiveColumns } =
        getComputedStyle(containerElement).gridTemplateColumns.split(' ');

      setEffectiveColumns(nextEffectiveColumns);
    }, [
      columns,
      basis,
      columnGap,
      rowGap,
      containerWidth,
      setEffectiveColumns
    ]);

    // column width may not be grater than the container width
    const effectiveBasis = Math.min(basis, containerWidth);

    // compute the grid columns template
    let gridTemplateColumns: string;

    if (columnsAuto) {
      gridTemplateColumns =
        justification === 'stretch'
          ? `repeat(auto-fill, minmax(${effectiveBasis}px, 1fr))`
          : `repeat(auto-fill, ${effectiveBasis}px)`;
    } else {
      gridTemplateColumns =
        justification === 'stretch'
          ? `repeat(${columns}, minmax(0px, 1fr))`
          : `repeat(${columns}, ${basis}px)`;
    }

    return (
      <GridContainer
        ref={containerMergedRef}
        style={{
          gridTemplateColumns,
          columnGap,
          rowGap,
          ...style
        }}
        {...forwardingProps}
        _justification={justification}
      >
        {Children.count(children) !== 0 ? children : <div />}
      </GridContainer>
    );
  }
);
